import {
  CardPosition,
  EnginePhase,
  Seat,
  ZoneRef,
  type DuelPromptCode,
} from './state';

// Events emitted by the engine describing WHAT changed. The client renderer animates them.
// Events are the only thing the renderer needs to play the match forward from a known state.

export enum EventType {
  CARD_DRAWN = 'CARD_DRAWN',
  CARD_MOVED = 'CARD_MOVED',
  CARD_SUMMONED = 'CARD_SUMMONED',
  CARD_SET = 'CARD_SET',
  CARD_FLIPPED = 'CARD_FLIPPED',
  POSITION_CHANGED = 'POSITION_CHANGED',
  CARD_DESTROYED = 'CARD_DESTROYED',
  CARD_TO_GRAVEYARD = 'CARD_TO_GRAVEYARD',
  CARD_BANISHED = 'CARD_BANISHED',
  CARD_TO_HAND = 'CARD_TO_HAND',
  STAT_CHANGED = 'STAT_CHANGED',
  SPELL_ACTIVATED = 'SPELL_ACTIVATED',
  TRAP_ACTIVATED = 'TRAP_ACTIVATED',
  EFFECT_RESOLVED = 'EFFECT_RESOLVED',
  ATTACK_DECLARED = 'ATTACK_DECLARED',
  ATTACK_REPLAY = 'ATTACK_REPLAY',
  BATTLE_RESOLVED = 'BATTLE_RESOLVED',
  LIFE_POINTS_CHANGED = 'LIFE_POINTS_CHANGED',
  PHASE_CHANGED = 'PHASE_CHANGED',
  TURN_CHANGED = 'TURN_CHANGED',
  REQUEST_TARGETS = 'REQUEST_TARGETS',
  /** A seat confirmed effect targets. Played before those cards move or resolve. */
  TARGETS_CHOSEN = 'TARGETS_CHOSEN',
  REQUEST_PRIORITY = 'REQUEST_PRIORITY',
  GAME_OVER = 'GAME_OVER',
  MESSAGE = 'MESSAGE',
  /** Private look at hidden cards (Stern Mystic, Ancient Telescope, Spy). */
  PEEK_CARDS = 'PEEK_CARDS',
  /** A seat's hand was reordered; `order` is uids only (no hidden ids). */
  HAND_SHUFFLED = 'HAND_SHUFFLED',
  /** A link joined a chain that now has 2+ links. Not emitted for a lone link. */
  CHAIN_LINK_ADDED = 'CHAIN_LINK_ADDED',
  /** A chain of 2+ links starts resolving. `links` is in resolution (LIFO) order. */
  CHAIN_RESOLVING = 'CHAIN_RESOLVING',
  /** One link of a 2+ chain resolves now (or fizzles). Its events follow. */
  CHAIN_LINK_RESOLVING = 'CHAIN_LINK_RESOLVING',
  /** Main Deck was shuffled after a search. No card ids. */
  DECK_SHUFFLED = 'DECK_SHUFFLED',
  /** One or more coin tosses. `call` is null when the card does not call it. */
  COIN_TOSSED = 'COIN_TOSSED',
}

export interface CardDrawnEvent {
  type: EventType.CARD_DRAWN;
  seat: Seat;
  cardUid: string;
  /** only revealed to the owner; opponents receive cardId === null. */
  cardId: string | null;
}

export interface CardMovedEvent {
  type: EventType.CARD_MOVED;
  cardUid: string;
  from: ZoneRef;
  to: ZoneRef;
}

export interface CardSummonedEvent {
  type: EventType.CARD_SUMMONED;
  seat: Seat;
  cardUid: string;
  cardId: string;
  zoneIndex: number;
  position: CardPosition;
  tribute: boolean;
  /**
   * Monsters already sent to the Graveyard for this summon. The board plays
   * the summon animation on each of them. It does not count as a Tribute Summon.
   */
  tributeUids?: string[];
}

export interface CardSetEvent {
  type: EventType.CARD_SET;
  seat: Seat;
  cardUid: string;
  zone: 'monsters' | 'spellTraps' | 'fieldSpell';
  zoneIndex: number;
  /** True when this Set is a face-down Tribute Summon. */
  tribute?: boolean;
}

export interface CardFlippedEvent {
  type: EventType.CARD_FLIPPED;
  cardUid: string;
  cardId: string;
  faceUp: boolean;
}

export interface PositionChangedEvent {
  type: EventType.POSITION_CHANGED;
  cardUid: string;
  position: CardPosition;
}

export interface CardDestroyedEvent {
  type: EventType.CARD_DESTROYED;
  cardUid: string;
}

export interface CardToGraveyardEvent {
  type: EventType.CARD_TO_GRAVEYARD;
  cardUid: string;
  seat: Seat;
}

export interface CardBanishedEvent {
  type: EventType.CARD_BANISHED;
  cardUid: string;
  seat: Seat;
  faceDown: boolean;
}

export interface CardToHandEvent {
  type: EventType.CARD_TO_HAND;
  cardUid: string;
  seat: Seat;
}

export interface StatChangedEvent {
  type: EventType.STAT_CHANGED;
  cardUid: string;
  atk: number;
  def: number;
}

export interface SpellActivatedEvent {
  type: EventType.SPELL_ACTIVATED;
  seat: Seat;
  cardUid: string;
  cardId: string;
}

export interface TrapActivatedEvent {
  type: EventType.TRAP_ACTIVATED;
  seat: Seat;
  cardUid: string;
  cardId: string;
}

export interface EffectResolvedEvent {
  type: EventType.EFFECT_RESOLVED;
  cardUid: string;
  detail: string;
}

export interface AttackDeclaredEvent {
  type: EventType.ATTACK_DECLARED;
  attackerUid: string;
  targetUid: string | null;
}

export interface AttackReplayEvent {
  type: EventType.ATTACK_REPLAY;
  attackerUid: string;
  targetUid: string | null;
}

export interface BattleResolvedEvent {
  type: EventType.BATTLE_RESOLVED;
  attackerUid: string;
  targetUid: string | null;
  attackerDestroyed: boolean;
  targetDestroyed: boolean;
  damageSeat: Seat | null;
  damage: number;
}

export interface LifePointsChangedEvent {
  type: EventType.LIFE_POINTS_CHANGED;
  seat: Seat;
  lifePoints: number;
  delta: number;
}

export interface PhaseChangedEvent {
  type: EventType.PHASE_CHANGED;
  phase: EnginePhase;
  turnPlayer: Seat;
}

export interface TurnChangedEvent {
  type: EventType.TURN_CHANGED;
  turnPlayer: Seat;
  turnCount: number;
  absoluteTurnCount: number;
}

export interface RequestTargetsEvent {
  type: EventType.REQUEST_TARGETS;
  requestId: string;
  seat: Seat;
  prompt: string;
  candidateUids: string[];
  min: number;
  max: number;
  /** Card that opened this picker (Last Will in the GY, Reborn on the field). */
  sourceUid?: string;
  sourceCardId?: string;
}

/** Targets a seat just confirmed. `seat` is the chooser, not the target's owner. */
export interface TargetsChosenEvent {
  type: EventType.TARGETS_CHOSEN;
  seat: Seat;
  targetUids: string[];
  sourceUid?: string;
  sourceCardId?: string;
}

export interface RequestPriorityEvent {
  type: EventType.REQUEST_PRIORITY;
  requestId: string;
  seat: Seat;
  prompt: string;
  promptCode: DuelPromptCode;
  activatableUids: string[];
}

/** Why an in-progress attack was cancelled. Interpolated into `attackCancelled`. */
export type AttackCancelReason = 'attackerGone' | 'targetGone' | 'oppHasMonster';

/**
 * Stable ids for `EventType.MESSAGE`. English `text` stays on the wire for
 * logs and old clients; the UI translates `code`.
 * `vars.cardId` is a passcode, never a display name.
 */
export type DuelMessageCode =
  | 'offeringPassed'
  | 'offeringAgain'
  | 'unsupportedAction'
  | 'discardDown'
  | 'flipActivated'
  | 'flipNoTarget'
  | 'attackCancelled'
  | 'wabokuDamage'
  | 'kuribohDamage'
  | 'activationNegated'
  | 'summonNegated'
  | 'wabokuAttacker'
  | 'wabokuDefender'
  | 'noResponse'
  | 'fizzled'
  | 'noLegalTarget'
  | 'reverseTrap'
  | 'returnedToExtra'
  | 'revealDestroyed'
  | 'revealSetAgain'
  | 'fakeTrapConfirm'
  | 'fakeTrapReplace'
  | 'guardianAtkZero'
  | 'coinCall'
  | 'timeWizardRight'
  | 'timeWizardWrong'
  | 'barrelHit'
  | 'barrelMiss'
  | 'jiraiRight'
  | 'jiraiWrong'
  | 'darkElfPaid'
  | 'electricLizardLock'
  | 'steelScorpionMark'
  | 'steelScorpionDestroy'
  | 'unhappyMaiden'
  | 'crassClown'
  | 'crassNoTarget'
  | 'dreamClown'
  | 'dreamNoTarget'
  | 'taintedWisdom'
  | 'yadoKaru'
  | 'cannonSoldier'
  | 'catapultTurtle'
  | 'aileBoost'
  | 'blastJuggler'
  | 'blastAsk'
  | 'blastNoTarget'
  | 'mushroomDrain'
  | 'mushroomAsk'
  | 'mushroomGive'
  | 'cheerfulCoffin'
  | 'tributeDoomed'
  | 'sharePain'
  | 'shareNoMonster'
  | 'searched'
  | 'thunderAdded'
  | 'immortalLoss'
  | 'swordTop'
  | 'equipStandby'
  | 'bigEye'
  | 'shieldSword'
  | 'discardedRandom'
  | 'noCardToDiscard'
  | 'battleDrew'
  | 'battleOppDrew'
  | 'specialSummoned'
  | 'egotistNoLady'
  | 'egotistNoZone'
  | 'egotistNoMonster'
  | 'cocoonEquipped';

/**
 * Stable ids for `EngineError`. The English sentence stays `Error.message`
 * so the wire and server logs do not change language.
 */
export type DuelErrorCode =
  | 'waitingTrapResponse'
  | 'discardFirst'
  | 'waitingFlipTarget'
  | 'waitingTarget'
  | 'waitingOffering'
  | 'waitingCoin'
  | 'waitingChainOrder'
  | 'offeringSummonOrPass'
  | 'offeringSummonOwed'
  | 'notYourTurn'
  | 'notMainPhase'
  | 'notBattlePhase'
  | 'notBattleStep'
  | 'attackResolving'
  | 'skipEndOnly'
  | 'cannotSkipEnd'
  | 'cardNotInHand'
  | 'notMonster'
  | 'fusionNoNormal'
  | 'noNormalSummon'
  | 'requiresTribute'
  | 'monsterZoneOccupied'
  | 'invalidZone'
  | 'fusionNoTribute'
  | 'noTributesNeeded'
  | 'needsExactTributes'
  | 'invalidTribute'
  | 'noFreeMonsterZone'
  | 'monsterNotFound'
  | 'noPositionSummonTurn'
  | 'positionAlreadyChanged'
  | 'monsterAlreadyAttacked'
  | 'dragonCaptureJar'
  | 'alreadyFaceUp'
  | 'noFlipSetTurn'
  | 'notSpellTrap'
  | 'noFreeSpellZone'
  | 'notSpell'
  | 'setSpellNotFound'
  | 'setTrapNotFound'
  | 'notTrap'
  | 'trapSetDelay'
  | 'noDamageStep'
  | 'cannotActivateNow'
  | 'conditionsNotMet'
  | 'noLegalTarget'
  | 'selectValidTargets'
  | 'selectValidTarget'
  | 'notEnoughLifePoints'
  | 'discardRequired'
  | 'tributeRequired'
  | 'noFlipPending'
  | 'notYourSelection'
  | 'invalidTarget'
  | 'noTargetPending'
  | 'attackerNotFound'
  | 'targetNotFound'
  | 'cannotDirectAttack'
  | 'attackerFaceDown'
  | 'attackerNotAttackPos'
  | 'alreadyAttacked'
  | 'noFirstTurnAttack'
  | 'swordsOfRevealingLight'
  | 'darkElfCost'
  | 'paralyzingPotion'
  | 'electricLizard'
  | 'ringOfMagnetism'
  | 'targetNotMonster'
  | 'noPendingResponse'
  | 'notYourResponse'
  | 'cardCannotActivateNow'
  | 'chainTooLong'
  | 'cannotChainSlower'
  | 'spellSpeed1NoChain'
  | 'noDiscardOwed'
  | 'cardNotYourHand'
  | 'normalSummonUsed'
  | 'offeringPhase'
  | 'notEnoughLp'
  | 'noOfferingMonster'
  | 'matchNotFound'
  | 'notInMatch'
  | 'invalidAction'
  | 'internalError';

export interface GameOverEvent {
  type: EventType.GAME_OVER;
  winner: Seat;
  reason: string;
}

export interface MessageEvent {
  type: EventType.MESSAGE;
  /** English sentence. Fallback when `code` is missing or unknown. */
  text: string;
  code?: DuelMessageCode;
  /** `cardId` is a Konami passcode. `kind` is `trap` | `spell`. */
  vars?: Record<string, string | number>;
}

export interface PeekCard {
  uid: string;
  cardId: string;
}

export interface PeekCardsEvent {
  type: EventType.PEEK_CARDS;
  /** Only this seat may see `cards` unless `reveal` is set. */
  seat: Seat;
  cards: PeekCard[];
  /**
   * Public reveal. Both seats receive the list. A field reveal (The Stern
   * Mystic) flips those cards. A search reveal (`search`) shows a card
   * added to the hand, from the Deck or from the Graveyard.
   */
  reveal?: boolean;
  /** Added to the hand. Both players must see the card. */
  search?: boolean;
}

export interface CoinTossedEvent {
  type: EventType.COIN_TOSSED;
  seat: Seat;
  cardUid: string;
  cardId: string;
  /** What the player called, or null for Barrel Dragon. */
  call: 'heads' | 'tails' | null;
  results: ('heads' | 'tails')[];
}

export interface HandShuffledEvent {
  type: EventType.HAND_SHUFFLED;
  seat: Seat;
  /** uids of the hand in the new order (no cardId: reveals nothing). */
  order: string[];
}

/** A chain link as the renderer sees it. Chain cards are always face-up. */
export interface ChainLinkView {
  link: number;
  controller: Seat;
  cardUid: string;
  cardId: string;
}

export interface ChainLinkAddedEvent {
  type: EventType.CHAIN_LINK_ADDED;
  /** The whole chain in activation order; the last entry is the new link. */
  links: ChainLinkView[];
}

export interface ChainResolvingEvent {
  type: EventType.CHAIN_RESOLVING;
  /** Highest link first. */
  links: ChainLinkView[];
}

export interface ChainLinkResolvingEvent extends ChainLinkView {
  type: EventType.CHAIN_LINK_RESOLVING;
  fizzled: boolean;
}

export interface DeckShuffledEvent {
  type: EventType.DECK_SHUFFLED;
  seat: Seat;
}

export type GameEvent =
  | CardDrawnEvent
  | CardMovedEvent
  | CardSummonedEvent
  | CardSetEvent
  | CardFlippedEvent
  | PositionChangedEvent
  | CardDestroyedEvent
  | CardToGraveyardEvent
  | CardBanishedEvent
  | CardToHandEvent
  | StatChangedEvent
  | SpellActivatedEvent
  | TrapActivatedEvent
  | EffectResolvedEvent
  | AttackDeclaredEvent
  | AttackReplayEvent
  | BattleResolvedEvent
  | LifePointsChangedEvent
  | PhaseChangedEvent
  | TurnChangedEvent
  | RequestTargetsEvent
  | TargetsChosenEvent
  | RequestPriorityEvent
  | GameOverEvent
  | MessageEvent
  | PeekCardsEvent
  | CoinTossedEvent
  | HandShuffledEvent
  | DeckShuffledEvent
  | ChainLinkAddedEvent
  | ChainResolvingEvent
  | ChainLinkResolvingEvent;
